import QuillLink from 'quill/formats/link';
import type Scroll from 'quill/blots/scroll';
import type Quill from 'quill/core';
import { MOD_KEY } from '../../constants/os';
import safeParseUrl from '../../utils/url/safeParseUrl';
import matchCurrentUrl from '../utils/matchCurrentUrl';
import findQuillByBlot from '../utils/findQuillByBlot';
import type ScrollToAnchor from '../ScrollToAnchor';
import type PostPreviewControls from '../PostPreviewControls';

const ALLOWED_PROTOCOLS = ['https:', 'http:', 'mailto:', 'tel:', 'webcal:'];
const { HOST } = window.__slab;

const setAttributes = (
  node: HTMLAnchorElement & { host?: string },
  defaultOpenInNewTab: boolean,
) => {
  const { hasMatchingOrigin } = matchCurrentUrl({
    target: node.href,
    currentUrl: window.location.href,
  });
  const shouldOpenInNewTab = !hasMatchingOrigin || defaultOpenInNewTab;

  if (shouldOpenInNewTab) {
    node.setAttribute('target', '_blank');
    if (node.host !== HOST) {
      node.setAttribute('rel', 'nofollow noopener noreferrer ugc');
    }
  } else {
    node.removeAttribute('target');
    node.removeAttribute('rel');
  }
};

function getModule(
  quill: Quill | null,
  name: 'postPreviewControls',
): PostPreviewControls | undefined;
function getModule(
  quill: Quill | null,
  name: 'scrollToAnchor',
): ScrollToAnchor | undefined;
function getModule(
  quill: Quill | null,
  name: 'postPreviewControls' | 'scrollToAnchor',
) {
  return quill?.getModule(name);
}

class Link extends QuillLink {
  protected static defaultNewTab = false;
  static className = 'ql-link';

  static create(value: string) {
    const node = super.create(value);
    setAttributes(node as HTMLAnchorElement, this.defaultNewTab);
    return node;
  }

  static sanitize(url: string) {
    const anchor = document.createElement('a');
    anchor.href = url;
    if (ALLOWED_PROTOCOLS.includes(anchor.protocol)) {
      if (!url.startsWith(anchor.protocol) && !url.startsWith('#')) {
        return `https://${url}`;
      }
      return url;
    }
    return 'about:blank';
  }

  constructor(scroll: Scroll, domNode: HTMLElement) {
    super(scroll, domNode);

    const quill = findQuillByBlot(this);

    const postPreviewControls = getModule(quill, 'postPreviewControls');
    const scrollToAnchor = getModule(quill, 'scrollToAnchor');

    domNode.addEventListener('click', (event) => {
      const href = this.statics.formats(this.domNode);
      if (typeof href !== 'string') return;

      const isPressingModKey = event[MOD_KEY];
      const readOnly = !scroll.isEnabled();

      if (isPressingModKey && !readOnly && href) {
        window.open(href, '_blank', 'noopener');
        event.preventDefault();
        return;
      }

      const currentUrl = postPreviewControls?.postUrl ?? window.location.href;
      const { hasCurrentPostId } = matchCurrentUrl({
        target: href,
        currentUrl,
      });

      // We can't manipulate the href of the link to make scrolling work,
      // because it would affect the value in LinkToolbar. Therefore,
      // we need to intercept the behavior and navigate to the hash manually.
      if (hasCurrentPostId && readOnly && !isPressingModKey) {
        const parsedUrl = safeParseUrl(href, window.location.href);

        if (parsedUrl?.hash) {
          if (postPreviewControls) {
            postPreviewControls.scrollToAnchor(parsedUrl.hash);
            event.preventDefault();
          } else if (scrollToAnchor) {
            window.location.hash = parsedUrl.hash;
            scrollToAnchor.scrollToAnchor(parsedUrl.hash);
            event.preventDefault();
          }
        }
      }
    });
  }

  format(name: string, value: string) {
    super.format(name, value);
    if (name === Link.blotName && value) {
      setAttributes(
        this.domNode as HTMLAnchorElement,
        this.statics.defaultNewTab,
      );
    }
  }
}

export default Link;
